import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Drawer,
  IconButton,
  CircularProgress,
  Snackbar,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Close,
  ChevronRight,
  Search,
  FileCopy,
  Check,
} from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { useAuth } from "../context/ApiKeyContext";
import { useTranslation } from "../hooks/useTranslation";
import usePermissionRedirect from "../hooks/usePermissionRedirect";
import { getApiBaseUrl } from "../config";
import { ENVIRONMENT_PRODUCTS_CATALOG } from "../utils/environmentProductsCatalog";

const API_BASE_URL = getApiBaseUrl();

function formatCNPJ(cnpj) {
  const c = String(cnpj || "").replace(/\D/g, "");
  if (c.length !== 14) return String(cnpj || "");
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
}

function stripCNPJ(cnpj) {
  return String(cnpj || "").replace(/\D/g, "");
}

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: "var(--ikaro-bg-page)",
  },
  content: {
    flex: 1,
    padding: theme.spacing(2, 2.5),
    overflow: "auto",
  },
  pageTitle: {
    fontWeight: 800,
    color: "var(--ikaro-text-primary)",
    marginBottom: theme.spacing(0.5),
  },
  pageSubtitle: {
    color: "var(--ikaro-text-secondary)",
    fontSize: "0.875rem",
  },
  tabBar: {
    marginBottom: theme.spacing(2),
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    borderRadius: 8,
    padding: 4,
    minHeight: 44,
    border: "1px solid var(--ikaro-border-light)",
  },
  tab: {
    textTransform: "none",
    minHeight: 40,
    fontWeight: 600,
    color: "var(--ikaro-text-secondary)",
    "&.Mui-selected": {
      color: "var(--ikaro-text-primary)",
    },
  },
  card: {
    padding: theme.spacing(2, 2.5),
  },
  searchWrap: {
    position: "relative",
    marginBottom: theme.spacing(2),
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--ikaro-gray-dark)",
    pointerEvents: "none",
  },
  searchInput: {
    paddingLeft: 40,
  },
  countBadge: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "0.75rem",
    color: "var(--ikaro-gray-dark)",
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--ikaro-gray-dark)",
    backgroundColor: "var(--ikaro-gray-light)",
  },
  rowClick: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
    },
  },
  copyBtn: {
    minWidth: 0,
    padding: "2px 8px",
  },
  productChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    maxWidth: 220,
  },
  warnBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: "1px solid var(--ikaro-status-warning-bg)",
    backgroundColor: "var(--ikaro-status-warning-bg)",
    color: "var(--ikaro-status-warning-text)",
    fontSize: "0.8125rem",
  },
  serviceBlock: {
    marginBottom: theme.spacing(1.5),
    border: "1px solid var(--ikaro-gray-medium)",
    borderRadius: 8,
    overflow: "hidden",
  },
  serviceHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 1.5),
    cursor: "pointer",
    userSelect: "none",
    backgroundColor: "var(--ikaro-gray-light)",
  },
  productGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5, 1.5, 4),
  },
  successBanner: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    border: "1px solid var(--ikaro-status-success-bg)",
    backgroundColor: "var(--ikaro-status-success-bg)",
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "flex-start",
  },
  resultRoot: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  resultHero: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3),
    borderRadius: 12,
    background: "linear-gradient(135deg, var(--ikaro-status-success-bg) 0%, var(--ikaro-bg-surface-secondary) 100%)",
    border: "1px solid var(--ikaro-status-success)",
  },
  resultHeroIcon: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    backgroundColor: "var(--ikaro-status-success)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  resultSection: {
    borderRadius: 10,
    border: "1px solid var(--ikaro-border-default)",
    backgroundColor: "var(--ikaro-bg-surface)",
    overflow: "hidden",
  },
  resultSectionHeader: {
    padding: theme.spacing(1.25, 2),
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    borderBottom: "1px solid var(--ikaro-border-light)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  resultSectionDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  resultSectionBody: {
    padding: theme.spacing(1.5, 2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  idRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 8,
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
  },
  idLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--ikaro-text-secondary)",
    minWidth: 80,
  },
  idValue: {
    flex: 1,
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "var(--ikaro-text-primary)",
    textAlign: "center",
  },
  apiKeyRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.25),
    borderRadius: 8,
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
  },
  apiKeyName: {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: "var(--ikaro-text-primary)",
    minWidth: 60,
    maxWidth: "40%",
    flexShrink: 0,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  apiKeyCode: {
    flex: 1,
    minWidth: 0,
    fontFamily: "monospace",
    fontSize: "0.78rem",
    color: "var(--ikaro-text-secondary)",
    wordBreak: "break-all",
    whiteSpace: "normal",
  },
  logRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    borderBottom: "1px solid var(--ikaro-border-light)",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  logBullet: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: "var(--ikaro-status-success)",
    flexShrink: 0,
    marginTop: 6,
  },
  drawerHeaderText: {
    minWidth: 0,
    flex: 1,
    paddingRight: theme.spacing(1),
  },
  drawerWrapText: {
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  envDetailDrawerPaper: {
    width: "min(440px, 100vw)",
    maxWidth: "100vw",
    boxSizing: "border-box",
    overflowY: "auto",
    overflowX: "hidden",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
}));

function CopyTextButton({ text, label, small, className }) {
  const [copied, setCopied] = useState(false);
  const handle = (e) => {
    e.stopPropagation();
    if (text == null) return;
    navigator.clipboard.writeText(String(text));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button
      size="small"
      className={className}
      onClick={handle}
      startIcon={
        copied ? <Check fontSize="small" /> : <FileCopy fontSize="small" />
      }
    >
      {small ? "" : copied ? "OK" : label}
    </Button>
  );
}

function productLabel(code) {
  for (const svc of ENVIRONMENT_PRODUCTS_CATALOG) {
    const p = svc.products.find((x) => x.code === code);
    if (p) return p.label;
  }
  return code;
}

/** Texto completo para colar após criação do ambiente (empresa, IDs, produtos, API keys, log). */
function buildEnvironmentSetupCopyText(createResult, form, flatProductCodes) {
  const lines = [];
  const company = createResult?.data?.company;
  const summary = createResult?.summary;
  const name =
    (company && company.name) ||
    summary?.company_name ||
    form?.razao_social ||
    "";
  const cnpjRaw = (company && company.cnpj) || summary?.cnpj || form?.cnpj || "";
  const cnpjFmt = formatCNPJ(stripCNPJ(cnpjRaw));

  lines.push("=== Empresa ===");
  lines.push(`Razão social / nome: ${name}`);
  lines.push(`CNPJ: ${cnpjFmt}`);
  if (summary?.company_id != null) lines.push(`Company ID: ${summary.company_id}`);
  else if (company?.id != null) lines.push(`Company ID: ${company.id}`);

  const tenants = createResult?.data?.tenants || [];
  lines.push("");
  lines.push("=== Tenants, células e API keys ===");
  tenants.forEach((t, ti) => {
    lines.push(`[Tenant ${ti + 1}] ID: ${t.id} — ${t.name || ""}`);
    (t.cells || []).forEach((c) => {
      lines.push(`  Cell: ${c.name || ""} (ID: ${c.id})`);
    });
    (t.api_keys || []).forEach((ak) => {
      lines.push(`  API key ${ak.name}: ${ak.key}`);
    });
    (t.users || []).forEach((u) => {
      lines.push(`  Usuário: ${u.name} <${u.email}> (ID: ${u.id}, status: ${u.status})`);
    });
  });

  if (flatProductCodes && flatProductCodes.length > 0) {
    lines.push("");
    lines.push("=== Produtos liberados ===");
    flatProductCodes.forEach((c) => {
      lines.push(`- ${productLabel(c)} (${c})`);
    });
  }

  const msgs = createResult?.data?.messages || [];
  if (msgs.length > 0) {
    lines.push("");
    lines.push("=== Log ===");
    msgs.forEach((m) => lines.push(m));
  }

  const emailGestor = (form?.email_super_admin || "").trim();
  const nomeGestor = (form?.nome_super_admin || "").trim();
  if (emailGestor || nomeGestor) {
    lines.push("");
    lines.push("=== Gestor informado no cadastro ===");
    if (nomeGestor) lines.push(`Nome: ${nomeGestor}`);
    if (emailGestor) lines.push(`E-mail: ${emailGestor}`);
  }

  return lines.join("\n");
}

function buildDetailDrawerCopyText(detail) {
  if (!detail) return "";
  const lines = [
    `Company ID: ${detail.company_id}`,
    `Tenant ID: ${detail.tenant_id}`,
    `Cell ID: ${detail.cell_id}`,
    `Razão social: ${detail.razao_social}`,
    `CNPJ: ${formatCNPJ(detail.cnpj)}`,
  ];
  const keys = detail.api_keys || [];
  if (keys.length > 0) {
    lines.push("");
    lines.push("API keys:");
    keys.forEach((ak) => lines.push(`  ${ak.name || ""}: ${ak.key}`));
  }
  return lines.join("\n");
}

const RULES_AMBIENTS_PAGE = ["admin", "ambients"];

function EnvironmentManagementPage() {
  usePermissionRedirect("ADMIN", RULES_AMBIENTS_PAGE);
  const classes = useStyles();
  const { token, hasRole } = useAuth();
  const { t } = useTranslation("environments");
  const tRef = useRef(t);
  tRef.current = t;

  const [tab, setTab] = useState(0);
  const [report, setReport] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);

  const [search, setSearch] = useState("");
  const [filterCompanyId, setFilterCompanyId] = useState("");
  const [filterTenantId, setFilterTenantId] = useState("");
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    razao_social: "",
    cnpj: "",
    nome_super_admin: "",
    email_super_admin: "",
  });
  const [selectedByService, setSelectedByService] = useState({});
  const [cnpjWarning, setCnpjWarning] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const canCreateEnvironments =
    hasRole("ADMIN", "admin") || hasRole("ADMIN", "ambients");

  useEffect(() => {
    if (!canCreateEnvironments && tab === 1) {
      setTab(0);
    }
  }, [canCreateEnvironments, tab]);

  // Não incluir `t` nas deps: o i18n troca a referência de `t` a cada render e re-dispara o efeito em loop.
  const loadReport = useCallback(async () => {
    if (!token) {
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    setListError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/super_admin/companies_tenants_cells_report`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setReport(data.report || []);
    } catch (e) {
      setListError(e.message);
      setSnack({
        open: true,
        message: tRef.current("errorLoad"),
        severity: "error",
      });
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const companyOptions = useMemo(() => {
    const m = new Map();
    report.forEach((e) => {
      if (!m.has(e.company_id)) {
        m.set(e.company_id, {
          id: e.company_id,
          name: e.company_name || "",
        });
      }
    });
    return Array.from(m.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }),
    );
  }, [report]);

  const tenantOptions = useMemo(() => {
    const rows = filterCompanyId
      ? report.filter((e) => String(e.company_id) === filterCompanyId)
      : report;
    const m = new Map();
    rows.forEach((e) => {
      if (!m.has(e.tenant_id)) {
        m.set(e.tenant_id, {
          id: e.tenant_id,
          name: e.tenant_name || "",
        });
      }
    });
    return Array.from(m.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }),
    );
  }, [report, filterCompanyId]);

  useEffect(() => {
    if (!filterTenantId) return;
    const valid = tenantOptions.some((o) => String(o.id) === filterTenantId);
    if (!valid) setFilterTenantId("");
  }, [tenantOptions, filterTenantId]);

  const filtered = useMemo(() => {
    let rows = report;
    if (filterCompanyId) {
      const cid = Number(filterCompanyId);
      rows = rows.filter((e) => e.company_id === cid);
    }
    if (filterTenantId) {
      const tid = Number(filterTenantId);
      rows = rows.filter((e) => e.tenant_id === tid);
    }
    if (!search.trim()) return rows;
    const s = search.toLowerCase().trim();
    const digits = stripCNPJ(search);
    return rows.filter((e) => {
      const name = (e.company_name || "").toLowerCase();
      const razao = (e.razao_social || "").toLowerCase();
      const tenantNm = (e.tenant_name || "").toLowerCase();
      const cellNm = (e.cell_name || "").toLowerCase();
      // Em JS, qualquer string .includes("") é true — só comparar CNPJ se houver dígitos na busca.
      const cnpjOk =
        digits.length > 0 && stripCNPJ(e.cnpj || "").includes(digits);
      const productsHit = (e.products || []).some((p) => {
        const code = String(p.code || "").toLowerCase();
        const pnm = String(p.name || "").toLowerCase();
        return code.includes(s) || pnm.includes(s);
      });
      return (
        name.includes(s) ||
        razao.includes(s) ||
        tenantNm.includes(s) ||
        cellNm.includes(s) ||
        productsHit ||
        cnpjOk ||
        String(e.tenant_id).includes(s) ||
        String(e.cell_id).includes(s) ||
        String(e.company_id).includes(s)
      );
    });
  }, [report, search, filterCompanyId, filterTenantId]);

  const handleCnpjChange = (value) => {
    const raw = stripCNPJ(value);
    let formatted = raw;
    if (raw.length > 2) formatted = `${raw.slice(0, 2)}.${raw.slice(2)}`;
    if (raw.length > 5)
      formatted = `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5)}`;
    if (raw.length > 8)
      formatted = `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5, 8)}/${raw.slice(8)}`;
    if (raw.length > 12)
      formatted = `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5, 8)}/${raw.slice(8, 12)}-${raw.slice(12, 14)}`;

    setForm((f) => ({ ...f, cnpj: formatted }));

    if (raw.length >= 11) {
      const found = report.find((e) => stripCNPJ(e.cnpj) === raw);
      setCnpjWarning(found || null);
    } else {
      setCnpjWarning(null);
    }
  };

  const toggleService = (serviceId, products) => {
    setSelectedByService((prev) => {
      const next = { ...prev };
      const active = next[serviceId]?.length;
      if (active) {
        delete next[serviceId];
      } else {
        next[serviceId] = products.map((p) => p.code);
      }
      return next;
    });
  };

  const toggleProduct = (serviceId, productCode) => {
    setSelectedByService((prev) => {
      const cur = prev[serviceId] || [];
      const has = cur.includes(productCode);
      const n = has ? cur.filter((c) => c !== productCode) : [...cur, productCode];
      const copy = { ...prev };
      if (n.length === 0) delete copy[serviceId];
      else copy[serviceId] = n;
      return copy;
    });
  };

  const flatProductCodes = useMemo(() => {
    const codes = [];
    Object.values(selectedByService).forEach((arr) => {
      arr.forEach((c) => codes.push(c));
    });
    return codes;
  }, [selectedByService]);

  const handleCreate = async () => {
    if (!form.razao_social || !form.cnpj || cnpjWarning) return;
    setSubmitting(true);
    try {
      const body = {
        razao_social: form.razao_social.trim(),
        cnpj: form.cnpj,
        nome_super_admin: form.nome_super_admin.trim() || null,
        email_super_admin: form.email_super_admin.trim() || null,
        product_codes: flatProductCodes.length ? flatProductCodes : null,
      };
      const res = await fetch(`${API_BASE_URL}/super_admin/setup_company_environment`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || data.message || `HTTP ${res.status}`);
      }
      setCreateResult(data);
      setSnack({ open: true, message: t("successTitle"), severity: "success" });
      loadReport();
    } catch (e) {
      setSnack({ open: true, message: e.message || t("errorCreate"), severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const resetCreate = () => {
    setCreateResult(null);
    setForm({
      razao_social: "",
      cnpj: "",
      nome_super_admin: "",
      email_super_admin: "",
    });
    setSelectedByService({});
    setCnpjWarning(null);
  };

  const detail = selected;

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <Typography variant="h5" component="h1" className={classes.pageTitle}>
          {t("title")}
        </Typography>
        <Typography className={classes.pageSubtitle}>{t("subtitle")}</Typography>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          className={classes.tabBar}
          indicatorColor="primary"
        >
          <Tab className={classes.tab} label={t("tabList")} />
          {canCreateEnvironments && (
            <Tab className={classes.tab} label={t("tabCreate")} />
          )}
        </Tabs>

        <Paper className={`ikaro-card ${classes.card}`} elevation={0}>
          {tab === 0 && (
            <>
              <Box display="flex" flexWrap="wrap" alignItems="flex-end" style={{ gap: 16 }} mb={2}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 260 }}>
                  <InputLabel shrink id="env-filter-company-label">
                    {t("filterCompany")}
                  </InputLabel>
                  <Select
                    labelId="env-filter-company-label"
                    label={t("filterCompany")}
                    value={filterCompanyId}
                    onChange={(e) => setFilterCompanyId(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === "") return t("filterAllCompanies");
                      const c = companyOptions.find((x) => String(x.id) === selected);
                      return c ? `${c.id} — ${c.name}` : selected;
                    }}
                  >
                    <MenuItem value="">{t("filterAllCompanies")}</MenuItem>
                    {companyOptions.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.id} — {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" style={{ minWidth: 260 }}>
                  <InputLabel shrink id="env-filter-tenant-label">
                    {t("filterTenant")}
                  </InputLabel>
                  <Select
                    labelId="env-filter-tenant-label"
                    label={t("filterTenant")}
                    value={filterTenantId}
                    onChange={(e) => setFilterTenantId(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === "") return t("filterAllTenants");
                      const o = tenantOptions.find((x) => String(x.id) === selected);
                      return o ? `${o.id} — ${o.name}` : selected;
                    }}
                  >
                    <MenuItem value="">{t("filterAllTenants")}</MenuItem>
                    {tenantOptions.map((o) => (
                      <MenuItem key={o.id} value={String(o.id)}>
                        {o.id} — {o.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <div className={classes.searchWrap}>
                <Search className={classes.searchIcon} fontSize="small" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={t("searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ className: classes.searchInput }}
                />
                <span className={classes.countBadge}>{filtered.length}</span>
              </div>

              {loadingList ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={32} />
                </Box>
              ) : listError ? (
                <Typography color="error">{listError}</Typography>
              ) : (
                <TableContainer style={{ maxHeight: 520 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeadCell}>
                          {t("table.companyId")}
                        </TableCell>
                        <TableCell className={classes.tableHeadCell}>
                          {t("table.company")}
                        </TableCell>
                        <TableCell className={classes.tableHeadCell}>
                          {t("table.cnpj")}
                        </TableCell>
                        <TableCell className={classes.tableHeadCell} align="center">
                          {t("table.tenant")}
                        </TableCell>
                        <TableCell className={classes.tableHeadCell} align="center">
                          {t("table.cell")}
                        </TableCell>
                        <TableCell className={classes.tableHeadCell}>
                          {t("table.products")}
                        </TableCell>
                        <TableCell className={classes.tableHeadCell} align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filtered.map((env) => (
                        <TableRow
                          key={`${env.company_id}-${env.tenant_id}-${env.cell_id}`}
                          className={classes.rowClick}
                          onClick={() => setSelected(env)}
                        >
                          <TableCell>{env.company_id}</TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              {env.company_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <code style={{ fontSize: "0.8rem" }}>
                              {formatCNPJ(env.cnpj)}
                            </code>
                          </TableCell>
                          <TableCell align="center">{env.tenant_id}</TableCell>
                          <TableCell align="center">{env.cell_id}</TableCell>
                          <TableCell>
                            <div className={classes.productChips}>
                              {(env.products || []).slice(0, 4).map((p) => (
                                <Chip
                                  key={p.code}
                                  size="small"
                                  label={p.name || p.code}
                                  style={{
                                    height: 22,
                                    fontSize: "0.7rem",
                                    backgroundColor: "var(--ikaro-status-success-bg)",
                                    color: "var(--ikaro-status-success-text)",
                                  }}
                                />
                              ))}
                              {(env.products || []).length > 4 && (
                                <Chip
                                  size="small"
                                  label={`+${(env.products || []).length - 4}`}
                                  style={{ height: 22, fontSize: "0.7rem" }}
                                />
                              )}
                            </div>
                          </TableCell>
                          <TableCell align="right">
                            <ChevronRight color="primary" fontSize="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {filtered.length === 0 && (
                    <Box py={4} textAlign="center" color="textSecondary">
                      {search.trim()
                        ? t("noResults", { term: search })
                        : filterCompanyId || filterTenantId
                          ? t("noResultsFilters")
                          : t("noResultsEmpty")}
                    </Box>
                  )}
                </TableContainer>
              )}
            </>
          )}

          {canCreateEnvironments && tab === 1 && !createResult && (
            <>
              <div className={classes.warnBox}>
                <strong>{t("attentionPrefix")}</strong> {t("attentionBox")}
              </div>

              <Box display="flex" flexWrap="wrap" style={{ gap: 16 }} mb={2}>
                <Box flex="1" minWidth={240}>
                  <Typography variant="caption" display="block" gutterBottom>
                    {t("fieldRazaoSocial")} *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={form.razao_social}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, razao_social: e.target.value }))
                    }
                  />
                </Box>
                <Box flex="1" minWidth={240}>
                  <Typography variant="caption" display="block" gutterBottom>
                    {t("fieldCnpj")} *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={form.cnpj}
                    onChange={(e) => handleCnpjChange(e.target.value)}
                    inputProps={{ maxLength: 18 }}
                    error={!!cnpjWarning}
                  />
                  {cnpjWarning && (
                    <Box mt={1} p={1} style={{ borderRadius: 8, backgroundColor: "var(--ikaro-status-warning-bg)" }}>
                      <Typography variant="caption" component="div">
                        {t("cnpjDuplicate")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t("cnpjDuplicateHint", {
                          name: cnpjWarning.company_name,
                          cid: cnpjWarning.company_id,
                          tid: cnpjWarning.tenant_id,
                          cellId: cnpjWarning.cell_id,
                        })}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                {t("gestorSectionTitle")}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                component="p"
                style={{ marginBottom: 12, maxWidth: 720 }}
              >
                {t("gestorSectionBody")}
              </Typography>
              <Box display="flex" flexDirection="column" style={{ gap: 16 }} mb={1}>
                <Box flex="1" minWidth={240}>
                  <Typography variant="caption" display="block" gutterBottom>
                    {t("fieldGestorEmail")}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="email"
                    autoComplete="email"
                    placeholder="nome@empresa.com"
                    value={form.email_super_admin}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email_super_admin: e.target.value }))
                    }
                  />
                </Box>
                <Box flex="1" minWidth={240}>
                  <Typography variant="caption" display="block" gutterBottom>
                    {t("fieldGestorNameOptional")}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={form.nome_super_admin}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, nome_super_admin: e.target.value }))
                    }
                  />
                </Box>
              </Box>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 16 }}>
                {t("gestorApiNote")}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                {t("servicesProducts")}
              </Typography>
              {ENVIRONMENT_PRODUCTS_CATALOG.map((svc) => {
                const codes = selectedByService[svc.id] || [];
                const active = codes.length > 0;
                const all = svc.products.every((p) => codes.includes(p.code));
                const some = active && !all;
                return (
                  <div key={svc.id} className={classes.serviceBlock}>
                    <div
                      className={classes.serviceHeader}
                      onClick={() => toggleService(svc.id, svc.products)}
                      role="presentation"
                    >
                      <Box
                        mr={1}
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          border: `2px solid var(--ikaro-orange-primary)`,
                          backgroundColor: active ? "var(--ikaro-orange-primary)" : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {all && (
                          <Check style={{ color: "#fff", fontSize: 14 }} />
                        )}
                        {some && (
                          <Box
                            style={{
                              width: 8,
                              height: 2,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {svc.label}
                      </Typography>
                      <Typography variant="caption" style={{ marginLeft: "auto", color: "#888" }}>
                        {t("productsCount", { n: codes.length, total: svc.products.length })}
                      </Typography>
                    </div>
                    {active && (
                      <div className={classes.productGrid}>
                        {svc.products.map((p) => {
                          const checked = codes.includes(p.code);
                          return (
                            <Button
                              key={p.code}
                              size="small"
                              variant={checked ? "contained" : "outlined"}
                              color={checked ? "primary" : "default"}
                              onClick={() => toggleProduct(svc.id, p.code)}
                            >
                              {p.label}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              <Box mt={2}>
                <Button
                  className="ikaro-btn ikaro-btn-primary ikaro-btn-md"
                  disabled={
                    !form.razao_social ||
                    !form.cnpj ||
                    !!cnpjWarning ||
                    submitting
                  }
                  onClick={handleCreate}
                >
                  {submitting ? (
                    <>
                      <CircularProgress size={18} color="inherit" style={{ marginRight: 8 }} />
                      {t("submitting")}
                    </>
                  ) : (
                    t("submit")
                  )}
                </Button>
              </Box>
            </>
          )}

          {canCreateEnvironments && tab === 1 && createResult && createResult.data && (
            <Box className={classes.resultRoot}>

              {/* Hero */}
              <div className={classes.resultHero}>
                <div className={classes.resultHeroIcon}>
                  <Check style={{ color: "#fff", fontSize: 26 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{ fontWeight: 800, fontSize: "1.05rem", color: "var(--ikaro-status-success-text)" }}>
                    {t("successTitle")}
                  </Typography>
                  <Typography variant="body2" style={{ color: "var(--ikaro-text-secondary)", marginTop: 2 }}>
                    {form.razao_social || createResult.summary?.company_name}
                  </Typography>
                </div>
                <CopyTextButton
                  text={buildEnvironmentSetupCopyText(
                    createResult,
                    form,
                    flatProductCodes,
                  )}
                  label={t("copyAll")}
                />
              </div>

              {/* IDs + Produtos lado a lado */}
              <Box display="flex" style={{ gap: 16, flexWrap: "wrap" }}>

                {/* IDs */}
                <Box flex="1" minWidth={220} className={classes.resultSection}>
                  <div className={classes.resultSectionHeader}>
                    <div className={classes.resultSectionDot} style={{ backgroundColor: "var(--ikaro-orange-primary)" }} />
                    <Typography style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ikaro-text-secondary)" }}>
                      {t("idsTitle")}
                    </Typography>
                  </div>
                  <div className={classes.resultSectionBody}>
                    {[
                      { label: "Company", value: createResult.summary?.company_id },
                      { label: "Tenant", value: createResult.data.tenants?.[0]?.id },
                      { label: "Cell", value: createResult.data.tenants?.[0]?.cells?.[0]?.id },
                    ].filter(r => r.value != null).map((row) => (
                      <div key={row.label} className={classes.idRow}>
                        <span className={classes.idLabel}>{row.label}</span>
                        <span className={classes.idValue}>{row.value}</span>
                        <CopyTextButton small text={String(row.value)} label="copy" />
                      </div>
                    ))}
                  </div>
                </Box>

                {/* Produtos liberados */}
                {flatProductCodes.length > 0 && (
                  <Box flex="1" minWidth={220} className={classes.resultSection}>
                    <div className={classes.resultSectionHeader}>
                      <div className={classes.resultSectionDot} style={{ backgroundColor: "var(--ikaro-purple-accent)" }} />
                      <Typography style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ikaro-text-secondary)" }}>
                        {t("productsSection")}
                      </Typography>
                    </div>
                    <div className={classes.resultSectionBody} style={{ flexDirection: "row", flexWrap: "wrap" }}>
                      {flatProductCodes.map((c) => (
                        <Chip
                          key={c}
                          size="small"
                          label={productLabel(c)}
                          style={{
                            backgroundColor: "var(--ikaro-purple-light)",
                            color: "var(--ikaro-purple-accent)",
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            height: 24,
                          }}
                        />
                      ))}
                    </div>
                  </Box>
                )}
              </Box>

              {/* API Keys */}
              {createResult.data.tenants?.[0]?.api_keys?.length > 0 && (
                <div className={classes.resultSection}>
                  <div className={classes.resultSectionHeader}>
                    <div className={classes.resultSectionDot} style={{ backgroundColor: "var(--ikaro-status-success)" }} />
                    <Typography style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ikaro-text-secondary)" }}>
                      {t("apiKeysTitle")}
                    </Typography>
                  </div>
                  <div className={classes.resultSectionBody}>
                    {createResult.data.tenants[0].api_keys.map((ak) => (
                      <div key={ak.id} className={classes.apiKeyRow}>
                        <span className={classes.apiKeyName}>{ak.name}</span>
                        <code className={classes.apiKeyCode}>{ak.key}</code>
                        <CopyTextButton text={ak.key} label="copy" small />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Log de mensagens (colapsável visualmente) */}
              {(createResult.data.messages || []).length > 0 && (
                <div className={classes.resultSection}>
                  <div className={classes.resultSectionHeader}>
                    <div className={classes.resultSectionDot} style={{ backgroundColor: "var(--ikaro-gray-dark)" }} />
                    <Typography style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ikaro-text-secondary)" }}>
                      Log
                    </Typography>
                  </div>
                  <div className={classes.resultSectionBody} style={{ maxHeight: 180, overflowY: "auto" }}>
                    {(createResult.data.messages || []).map((m, i) => (
                      <div key={i} className={classes.logRow}>
                        <div className={classes.logBullet} />
                        <Typography variant="caption" style={{ color: "var(--ikaro-text-secondary)", lineHeight: 1.5 }}>
                          {m}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Box>
                <Button className="ikaro-btn ikaro-btn-secondary ikaro-btn-md" onClick={resetCreate}>
                  {t("createAnother")}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      <Drawer
        anchor="right"
        open={!!detail}
        onClose={() => setSelected(null)}
        PaperProps={{ className: classes.envDetailDrawerPaper }}
      >
        <Box width="100%" maxWidth="100%" p={2} boxSizing="border-box">
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <div className={classes.drawerHeaderText}>
              <Typography variant="caption" color="primary" style={{ fontWeight: 700 }}>
                {t("detailTitle")}
              </Typography>
              <Typography variant="h6" className={classes.drawerWrapText}>
                {detail?.company_name}
              </Typography>
            </div>
            <IconButton size="small" onClick={() => setSelected(null)}>
              <Close />
            </IconButton>
          </Box>

          {detail && (
            <>
              <Box mb={2}>
                <CopyTextButton text={buildDetailDrawerCopyText(detail)} label={t("copyAll")} />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                {t("idsSection")}
              </Typography>
              <Paper variant="outlined" style={{ padding: 12, marginBottom: 16 }}>
                {["company_id", "tenant_id", "cell_id"].map((k) => (
                  <Typography
                    key={k}
                    variant="body2"
                    gutterBottom
                    className={classes.drawerWrapText}
                  >
                    {k}: {detail[k]}{" "}
                    <CopyTextButton small text={String(detail[k])} label="copy" />
                  </Typography>
                ))}
              </Paper>

              <Typography variant="subtitle2" gutterBottom>
                {t("companySection")}
              </Typography>
              <Paper variant="outlined" style={{ padding: 12, marginBottom: 16 }}>
                <Typography variant="body2" className={classes.drawerWrapText}>
                  {detail.razao_social}
                </Typography>
                <Typography variant="body2" className={classes.drawerWrapText}>
                  <code>{formatCNPJ(detail.cnpj)}</code>
                </Typography>
              </Paper>

              <Typography variant="subtitle2" gutterBottom>
                {t("tenantCellSection")}
              </Typography>
              <Paper variant="outlined" style={{ padding: 12, marginBottom: 16 }}>
                <Typography variant="body2" className={classes.drawerWrapText}>
                  {t("tenant")}: {detail.tenant_name}
                </Typography>
                <Typography variant="body2" className={classes.drawerWrapText}>
                  {t("cell")}: {detail.cell_name}
                </Typography>
              </Paper>

              <Typography variant="subtitle2" gutterBottom>
                {t("apiKeysTitle")}
              </Typography>
              <Paper variant="outlined" style={{ padding: 12, marginBottom: 16 }}>
                {(detail.api_keys || []).length === 0 && (
                  <Typography variant="caption" color="textSecondary">
                    —
                  </Typography>
                )}
                {(detail.api_keys || []).map((ak) => (
                  <div key={ak.id} className={classes.apiKeyRow}>
                    <span className={classes.apiKeyName}>{ak.name}</span>
                    <code className={classes.apiKeyCode}>{ak.key}</code>
                    <CopyTextButton text={ak.key} label="copy" small />
                  </div>
                ))}
              </Paper>

              <Typography variant="subtitle2" gutterBottom>
                {t("productsSection")}
              </Typography>
              <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                {(detail.products || []).length === 0 && (
                  <Typography variant="caption" color="textSecondary">
                    —
                  </Typography>
                )}
                {(detail.products || []).map((p) => (
                  <Chip key={p.code} size="small" label={p.name || p.code} />
                ))}
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EnvironmentManagementPage;
